// Tag normalisation — "github", "Github" and "GitHub" are one tag.
//
// Matching is case-insensitive; the *display* spelling is picked once per tag
// (canonical map) so the whole vault agrees on it. Pure functions only.

const collapse = (t) => String(t == null ? '' : t).trim().replace(/\s+/g, ' ');

/** Comparison key for a tag: trimmed, single-spaced, lowercase. */
export const tagKey = (t) => collapse(t).toLowerCase();

/** Case-insensitive unique list; the first spelling wins, empties are dropped. */
export function dedupeTags(tags) {
  const seen = new Set();
  const out = [];
  (tags || []).forEach((t) => {
    const display = collapse(t);
    const key = display.toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(display);
  });
  return out;
}

/**
 * Chooses one display spelling per tag key.
 * Order of preference: built-in app spelling → most used → all-lowercase → alphabetical.
 * @param {Array<{tags?: string[]}>} cards
 * @param {string[]} [builtInTags]
 * @returns {Map<string, string>} key → canonical spelling
 */
export function buildCanonicalMap(cards, builtInTags = []) {
  const builtIn = new Map(builtInTags.map((t) => [tagKey(t), collapse(t)]));
  const variants = new Map(); // key → Map(spelling → uses)
  (cards || []).forEach((c) => {
    dedupeExact(c.tags).forEach((t) => {
      const key = tagKey(t);
      if (!key) return;
      const counts = variants.get(key) || new Map();
      counts.set(t, (counts.get(t) || 0) + 1);
      variants.set(key, counts);
    });
  });

  const map = new Map();
  variants.forEach((counts, key) => {
    if (builtIn.has(key)) {
      map.set(key, builtIn.get(key));
      return;
    }
    const best = [...counts.entries()].sort(([a, na], [b, nb]) => {
      if (nb !== na) return nb - na;
      const aLower = a === a.toLowerCase();
      const bLower = b === b.toLowerCase();
      if (aLower !== bLower) return aLower ? -1 : 1;
      return a.localeCompare(b);
    })[0][0];
    map.set(key, best);
  });
  builtIn.forEach((spelling, key) => {
    if (!map.has(key)) map.set(key, spelling);
  });
  return map;
}

// Whitespace-collapsed list without removing case variants (they are counted separately).
const dedupeExact = (tags) => [...new Set((tags || []).map(collapse).filter(Boolean))];

/** Maps a typed/suggested tag onto the vault's existing spelling, if there is one. */
export function resolveTag(tag, canonicalMap) {
  const display = collapse(tag);
  return canonicalMap.get(display.toLowerCase()) || display;
}

/**
 * Rewrites every card so case variants collapse to the canonical spelling.
 * Returns new card objects only where something changed.
 * @returns {{ cards: object[], changed: object[] }}
 */
export function mergeCardTags(cards, builtInTags = []) {
  const map = buildCanonicalMap(cards, builtInTags);
  const changed = [];
  const merged = (cards || []).map((c) => {
    if (!Array.isArray(c.tags)) return c;
    const next = dedupeTags(c.tags.map((t) => resolveTag(t, map)));
    const same = next.length === c.tags.length && next.every((t, i) => t === c.tags[i]);
    if (same) return c;
    const updated = { ...c, tags: next };
    changed.push(updated);
    return updated;
  });
  return { cards: merged, changed };
}
